import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  InputAdornment,
  Box,
  FormControl,
  Select,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Search } from "@mui/icons-material";

import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import Type from "./Type";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const AddHtPatientProcedure = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const [open, setOpen] = useState(null);
  const [type, setType] = useState("");

  // Patient search state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);

  const [formData, setFormData] = useState({
    type: "",
    procedure_date: "",
    procedure_day: "",
    status: "",
    period: "",
    doctors: [],
    lab: [],
    total_cost: "",
    advance: "",
    exchange_rate: "",
    currency: "",
    advance_currency: "",
    zone: "",
    description: "",
    patient: null,
    next_visit_days: "",
    next_visit_date: "",
    discount: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const [doctorsList, setDoctorsList] = useState([]);
  const [labsList, setLabsList] = useState([]);

  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);

  const {
    data: users,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useFetchObjects(["users"], "users/?position=7", token);

  const {
    data: types,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], `procedure-type/?type=1`, token);

  const {
    data: patientsData,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    refetch: refetchPatients,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  const {
    data: periods,
    isLoading: isLoadingPeriod,
    isError: isErrorPeriod,
  } = useFetchObjects(["period"], "period", token);

  const {
    data: labs,
    isLoading: isLoadingLab,
    isError: isErrorLab,
  } = useFetchObjects(["lab"], "lab", token);

  const { handleAdd, loading, resetForm } = useAdd("ht-patients", token);

  const { handleDelete, ConfirmDialog } = useDelete("procedure-type", token);
  const {
    handleDelete: handleDeletePeriod,
    ConfirmDialog: PeriodConfirmDialog,
  } = useDelete("period", token);
  const { handleDelete: handleDeleteLab, ConfirmDialog: LabConfirmDialog } =
    useDelete("lab", token);

  useEffect(() => {
    if (users) {
      setDoctorsList(users);
    }
  }, [users]);

  useEffect(() => {
    if (labs) {
      setLabsList(labs);
    }
  }, [labs]);

  // Patient data handling
  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        // Remove duplicates based on patient ID
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, []);

  useEffect(() => {
    if (searchTerm !== "") {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    } else {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, [searchTerm, refetchPatients]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        type: "",
        procedure_date: "",
        procedure_day: "",
        status: "",
        period: "",
        doctors: [],
        lab: [],
        total_cost: "",
        advance: "",
        exchange_rate: "",
        currency: "",
        advance_currency: "",
        zone: "",
        description: "",
        patient: null,
        next_visit_days: "",
        next_visit_date: "",
        discount: "",
      });
      setPatientInputValue("");
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      navigate("/htPatient/view-procedure");
    }
  }, [resetForm, navigate]);

  useEffect(() => {
    const newSelected = doctorsList.filter((doc) =>
      formData.doctors.includes(doc.id)
    );
    setSelectedDoctors(newSelected);
  }, [formData.doctors, doctorsList]);

  useEffect(() => {
    const newSelected = labsList.filter((lab) => formData.lab.includes(lab.id));
    setSelectedLabs(newSelected);
  }, [formData.lab, labsList]);

  // Auto-calculate next_visit_date
  useEffect(() => {
    if (formData.next_visit_days) {
      const daysToAdd = parseInt(formData.next_visit_days, 10);
      if (!isNaN(daysToAdd)) {
        const baseDate = formData.procedure_date
          ? new Date(formData.procedure_date)
          : new Date();
        baseDate.setDate(baseDate.getDate() + daysToAdd);
        const year = baseDate.getFullYear();
        const month = ("0" + (baseDate.getMonth() + 1)).slice(-2);
        const day = ("0" + baseDate.getDate()).slice(-2);
        const formattedDate = `${year}-${month}-${day}`;
        setFormData((prev) => ({
          ...prev,
          next_visit_date: formattedDate,
        }));
      }
    }
  }, [formData.procedure_date, formData.next_visit_days]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" })); // clear error on input
  };

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
    setFormErrors((prev) => ({ ...prev, patient: "" })); // clear patient error
  };

  const handlePatientChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, patient: newValue }));
    setFormErrors((prev) => ({ ...prev, patient: "" }));
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatient
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced setSearchTerm
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Autocomplete: Doctors
  const handleDoctorsChange = (event, newValue) => {
    setSelectedDoctors(newValue);
    const docIds = newValue.map((doc) => doc.id);
    setFormData((prev) => ({ ...prev, doctors: docIds }));
  };

  // Autocomplete: Labs
  const handleLabsChange = (event, newValue) => {
    setSelectedLabs(newValue);
    const labIds = newValue.map((lab) => lab.id);
    setFormData((prev) => ({ ...prev, lab: labIds }));
  };

  const handleAddNewLab = () => {
    setType("lab");
    setOpen(!open);
    const newLab = {
      id: Date.now(),
      name: "New Lab (untitled)",
    };
    setLabsList((prev) => [...prev, newLab]);
  };

  // Validation function for patient, type, currency
  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.patient) {
      errors.patient = t("call_center.patientRequired", "Patient is required.");
      isValid = false;
    }
    if (!formData.type) {
      errors.type = t("call_center.typeRequired", "Type is required.");
      isValid = false;
    }
    if (!formData.currency) {
      errors.currency = t(
        "call_center.currencyRequired",
        "Currency is required."
      );
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErrors({});

    if (!validateForm()) return;

    const submitData = new FormData();
    submitData.append("type", formData.type || "");
    if (formData.procedure_date) {
      submitData.append("procedure_date", formData.procedure_date);
    }
    submitData.append("procedure_day", formData.procedure_day);
    submitData.append("status", formData.status);
    submitData.append("period", formData.period);
    submitData.append("total_cost", formData.total_cost);
    submitData.append("advance", formData.advance);
    submitData.append("exchange_rate", formData.exchange_rate);
    submitData.append("currency", formData.currency);
    submitData.append("advance_currency", formData.advance_currency);
    submitData.append("zone", formData.zone);
    submitData.append("description", formData.description);
    submitData.append("patient", formData.patient?.id || "");
    submitData.append("next_visit_date", formData.next_visit_date);
    submitData.append("discount", formData.discount);

    formData.doctors.forEach((docId) => {
      submitData.append("doctors", docId);
    });
    formData.lab.forEach((labId) => {
      submitData.append("lab", labId);
    });

    handleAdd(submitData);
  };

  const handleOpenDialog = () => {
    setOpen(!open);
  };

  // Force update autocomplete if searchTerm cleared
  const forceUpdateAutocomplete = () => {
    setForceUpdate((prev) => prev + 1);
  };

  useEffect(() => {
    if (searchTerm === "") {
      forceUpdateAutocomplete();
    }
  }, [searchTerm]);

  return (
    <Grid container justifyContent="center">
      <Grid item xs={11.5} mt={2}>
        <Paper
          sx={{ padding: "20px", borderRadius: "10px", boxShadow: 3 }}
          component="form"
          onSubmit={handleSubmit}
        >
          {/* Go Back Icon and Title */}
          <Box display="flex" alignItems="center" mb={4}>
            <Tooltip title={t("pharmacy.goBack")} arrow>
              <IconButton
                onClick={() => navigate(-1)}
                aria-label={t("pharmacy.goBack")}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h5"
              gutterBottom
              fontWeight={"bold"}
              sx={{ borderBottom: "3px solid #007471", pb: 1.5 }}
            >
              {t("call_center.addNewHtPatient")}
            </Typography>
          </Box>

          <Grid container spacing={3} mt={1}>
            {/* PATIENT */}
            <Grid item xs={12} md={4}>
              <FormLabel required>{t("call_center.patient")}</FormLabel>
              <Autocomplete
                options={allPatients}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname || ""} - ${
                    option.phone || "N/A"
                  }`
                }
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                value={formData.patient}
                ListboxProps={{
                  onScroll: handleScroll,
                }}
                loading={isLoadingPatient}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t("call_center.patient")}
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {isLoadingPatient ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                filterOptions={(options, { inputValue }) => {
                  const input = inputValue.trim().toLowerCase();
                  return options.filter((option) => {
                    const nameMatch = `${option.firstname} ${
                      option.lastname || ""
                    }`
                      .toLowerCase()
                      .includes(input);
                    const phoneMatch = option.phone
                      ? option.phone.includes(input)
                      : false;
                    return nameMatch || phoneMatch;
                  });
                }}
              />
            </Grid>

            {/* TYPE */}
            <Grid item xs={12} md={4}>
              <FormLabel required>{t("call_center.type")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="type"
                value={formData.type}
                onChange={(e) => {
                  handleInputChange(e);
                  setFormErrors((prev) => ({ ...prev, type: "" }));
                }}
                error={!!formErrors.type}
                helperText={formErrors.type}
              >
                {isLoadingType && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorType && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {types?.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>{item.name}</Typography>
                      {formData.type !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Other fields (no validation) */}
            {/* ... add the rest as in your original code (procedure_day, procedure_date, status, period, doctors, lab, etc.) */}
            {/* PROCEDURE DAY */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel>{t("call_center.procedureDay")}</FormLabel>
                <Select
                  labelId="procedure_date-label"
                  id="procedure_date"
                  name="procedure_day"
                  value={formData.procedure_day || ""}
                  onChange={handleInputChange}
                  label={t("call_center.procedureDay")}
                >
                  <MenuItem value="monday">{t("call_center.monday")}</MenuItem>
                  <MenuItem value="tuesday">
                    {t("call_center.tuesday")}
                  </MenuItem>
                  <MenuItem value="wednesday">
                    {t("call_center.wednesday")}
                  </MenuItem>
                  <MenuItem value="thursday">
                    {t("call_center.thursday")}
                  </MenuItem>
                  <MenuItem value="friday">{t("call_center.friday")}</MenuItem>
                  <MenuItem value="saturday">
                    {t("call_center.saturday")}
                  </MenuItem>
                  <MenuItem value="sunday">{t("call_center.sunday")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* PROCEDURE DATE */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.procedureDate")}</FormLabel>
              <TextField
                fullWidth
                type="datetime-local"
                variant="outlined"
                name="procedure_date"
                value={formData.procedure_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* STATUS */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.status")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.done")}</MenuItem>
                <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
                <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
                <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
                <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
                <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
                <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
                <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
                <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
                <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
                <MenuItem value={11}>{t("call_center.information")}</MenuItem>
                <MenuItem value={12}>{t("call_center.comingSoon")}</MenuItem>
                <MenuItem value={13}>{t("call_center.block")}</MenuItem>
              </TextField>
            </Grid>

            {/* PERIOD */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.period")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="period"
                value={formData.period}
                onChange={handleInputChange}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setType("period");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewPeriod")}
                </Button>
                {isLoadingPeriod && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorPeriod && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {periods?.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>{item.name}</Typography>
                      {formData.period !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePeriod(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DOCTORS */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.doctors")}</FormLabel>
              {isLoadingUser ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : isErrorUser ? (
                <Typography color="error">
                  {t("call_center.errorField")}
                </Typography>
              ) : (
                <Autocomplete
                  multiple
                  options={doctorsList}
                  value={selectedDoctors}
                  onChange={handleDoctorsChange}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}` ||
                    "Unknown Doctor"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={t("call_center.selectDoctors")}
                    />
                  )}
                />
              )}
            </Grid>

            {/* LABS */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.lab")}</FormLabel>
              {isLoadingLab ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : isErrorLab ? (
                <Typography color="error">
                  {t("call_center.errorField")}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      textTransform: "capitalize",
                      padding: "13px 0px",
                    }}
                    onClick={handleAddNewLab}
                  >
                    {t("call_center.new", "New")}
                  </Button>
                  <Autocomplete
                    fullWidth
                    multiple
                    options={labsList}
                    value={selectedLabs}
                    onChange={handleLabsChange}
                    getOptionLabel={(option) => option.name || "Untitled Lab"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={t("call_center.selectLabs")}
                      />
                    )}
                  />
                </Box>
              )}
            </Grid>

            {/* ZONE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.zone")}</FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="zone"
                value={formData.zone}
                onChange={handleInputChange}
              />
            </Grid>

            {/* TOTAL PRICE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.totalPrice")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="total_cost"
                value={formData.total_cost}
                onChange={handleInputChange}
              />
            </Grid>

            {/* CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel required>{t("call_center.currency")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="currency"
                value={formData.currency}
                onChange={(e) => {
                  handleInputChange(e);
                  setFormErrors((prev) => ({ ...prev, currency: "" }));
                }}
                error={!!formErrors.currency}
                helperText={formErrors.currency}
              >
                <MenuItem value="">
                  {t("call_center.selectCurrency", "Select currency")}
                </MenuItem>
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* ADVANCE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.advance")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="advance"
                value={formData.advance}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Advance CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.advanceCurrency", "Advance Currency")}
              </FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="advance_currency"
                value={formData.advance_currency}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* DISCOUNT */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.discount")} (%)</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="discount"
                value={formData.discount}
                onChange={handleInputChange}
              />
            </Grid>

            {/* EXCHANGE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.exchangeRate")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="exchange_rate"
                value={formData.exchange_rate}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DAYS */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDays")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="next_visit_days"
                value={formData.next_visit_days}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DATE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDate")}</FormLabel>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                name="next_visit_date"
                value={formData.next_visit_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* DESCRIPTION */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.description", "Description")}
              </FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="description"
                value={formData?.description}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* SUBMIT BUTTON */}
            <Grid item xs={12} textAlign="end">
              <LoadingButton
                loading={loading}
                variant="contained"
                color="success"
                type="submit"
                style={{ padding: "10px 20px" }}
              >
                {t("call_center.save")}
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <ConfirmDialog />
      <LabConfirmDialog />
      <PeriodConfirmDialog />
      <Type open={open} onClose={handleOpenDialog} token={token} type={type} />
    </Grid>
  );
};

export default AddHtPatientProcedure;
